import {
    IMappingProfile,
    IQueryParams,
    IQueryParamsSymbol,
    PFWmsEntities,
} from '@pf/shared-common';
import { createMap, forMember, mapFrom, Mapper } from '@automapper/core';
import {
    entitySymbols,
    withDefaultEntityMetadata,
    withDefaultSearchParamsMappingConfiguration,
    withDefaultSearchParamsMetadata,
    withFragmentSearchFilter,
    withMetadata,
    withSearchFilter,
} from '@pf/shared-utility';
import {
    WarehouseItemSettingCreate,
    WarehouseItemSettingDto,
    WarehouseItemSettingRead,
    WarehouseItemSettingSearchParams,
} from '../../entities/warehouse-item-setting.dto';
import {
    CustomFieldMetadataMap,
    ResourceAuthSymbol,
} from '@pf/shared/util-platform';
import { withWarehouseSearchFilter } from '../wms-mapping.util';

const symbols = entitySymbols(PFWmsEntities.WarehouseItemSetting);

withDefaultEntityMetadata<WarehouseItemSettingDto>(symbols.dto, {
    itemId: String,
    warehouseId: String,
    primaryMinQuantity: Number,
    primaryMaxQuantity: Number,
    secondaryMinQuantity: Number,
    secondaryMaxQuantity: Number,
    totalWarehouseMin: Number,
    totalWarehouseMax: Number,
    criticalMin: Number,
    leadTimeDays: Number,
    safetyStockQuantity: Number,
    resourceAuth: ResourceAuthSymbol,
    customFields: CustomFieldMetadataMap.symbols.dto,
    equipmentType: Object,
});

withDefaultEntityMetadata<WarehouseItemSettingRead>(symbols.entity, {
    itemId: String,
    warehouseId: String,
    primaryMinQuantity: Number,
    primaryMaxQuantity: Number,
    secondaryMinQuantity: Number,
    secondaryMaxQuantity: Number,
    totalWarehouseMin: Number,
    totalWarehouseMax: Number,
    criticalMin: Number,
    leadTimeDays: Number,
    safetyStockQuantity: Number,
    resourceAuth: ResourceAuthSymbol,
    customFields: CustomFieldMetadataMap.symbols.entity,
    equipmentType: Object,
});

withMetadata<WarehouseItemSettingCreate>(symbols.createBody, {
    itemId: String,
    warehouseId: String,
    primaryMinQuantity: Number,
    primaryMaxQuantity: Number,
    secondaryMinQuantity: Number,
    secondaryMaxQuantity: Number,
    totalWarehouseMin: Number,
    totalWarehouseMax: Number,
    criticalMin: Number,
    leadTimeDays: Number,
    safetyStockQuantity: Number,
    resourceAuth: ResourceAuthSymbol,
    equipmentTypeId: String,
});

withDefaultSearchParamsMetadata<WarehouseItemSettingSearchParams>(
    symbols.searchParams,
    {
        itemId: String,
        warehouseId: String,
        equipmentTypeId: String,
    }
);

export const WarehouseItemSettingsMetadataMap: IMappingProfile = {
    symbols,
    profile: (mapper: Mapper) => {
        createMap<WarehouseItemSettingRead, WarehouseItemSettingDto>(
            mapper,
            symbols.entity,
            symbols.dto
        );
        createMap<WarehouseItemSettingDto, WarehouseItemSettingRead>(
            mapper,
            symbols.dto,
            symbols.entity
        );
        createMap<WarehouseItemSettingDto, WarehouseItemSettingCreate>(
            mapper,
            symbols.dto,
            symbols.createBody,
            forMember(
                p => p.equipmentTypeId,
                mapFrom(p => p.equipmentType?.id ?? '')
            )
        );
        createMap<
            IQueryParams<WarehouseItemSettingDto>,
            WarehouseItemSettingSearchParams
        >(
            mapper,
            IQueryParamsSymbol,
            symbols.searchParams,
            withSearchFilter('itemId'),
            withWarehouseSearchFilter(),
            withFragmentSearchFilter('equipmentType'),
            ...withDefaultSearchParamsMappingConfiguration()
        );
    },
};
